// stores/useStyleStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { styleApi, styleCategoryApi, productionTypeApi } from '@/api'

export const useStyleStore = defineStore('style', () => {
  // ===== State =====
  const globalStyles = ref([])
  const tempColors = ref([])
  const selectedStyleId = ref(null)

  // ===== 分类和生产类型 =====
  const categories = ref([])
  const productionTypes = ref([])

  // ✅ 分页信息
  const pagination = ref({
    page: 1,
    pageSize: 9999,
    total: 0,
    totalPages: 0
  })

  // ✅ 搜索条件
  const searchParams = ref({
    search: '',
    categoryId: '',
  })

  // ✅ 保留：需要查询方法的 getter
  const getStyleById = computed(() => {
    return (id) => globalStyles.value.find(s => s.id === id)
  })

  const getStyleByName = computed(() => {
    return (name) => globalStyles.value.find(s => s.name === name)
  })

  function getStyleName(styleId) {
    if (!styleId) return ''
    const style = globalStyles.value.find(s => s.id === styleId)
    return style ? style.name : ''
  }

  // ===== 分类相关 =====
  const level1List = computed(() => {
    return categories.value.filter(c => c.parent_id === null)
  })

  // 一级分类选项（返回 ID）
  const level1Options = computed(() => {
    return level1List.value.map(c => ({
      label: c.name,
      value: c.id
    }))
  })

  // 根据一级分类 ID 获取二级分类
  function getLevel2ByLevel1Id(level1Id) {
    if (!level1Id) return []
    return categories.value.filter(c => c.parent_id === level1Id)
  }

  // 二级分类选项（返回 ID）
  function getLevel2Options(level1Id) {
    return getLevel2ByLevel1Id(level1Id).map(c => ({
      label: c.name,
      value: c.id
    }))
  }

  // ✅ 获取所有二级分类选项（不区分一级分类）
  function getAllLevel2Options() {
    const level2List = categories.value.filter(c => c.parent_id !== null)
    return level2List.map(c => ({
      label: c.name,
      value: c.id
    }))
  }

  // 分类筛选选项（用于筛选栏）
  const categoryFilterOptions = computed(() => {
    const result = [
      { label: '全部分类', value: '' },
      ...categories.value.map(c => ({
        label: c.parent_id ? '  └─ ' + c.name : c.name,
        value: c.id
      }))
    ]
    return result
  })

  // ===== 生产类型相关 =====
  const productionTypeOptions = computed(() => {
    return productionTypes.value.map(t => ({
      label: t.name,
      value: t.id
    }))
  })

  // ===== 加载分类 =====
  async function loadCategories() {
    try {
      const data = await styleCategoryApi.getList()
      if (data && data.success) {
        categories.value = data.data || []
        return categories.value
      }
      return []
    } catch (err) {
      console.error('加载分类失败:', err)
      return []
    }
  }

  // ===== 加载生产类型 =====
  async function loadProductionTypes() {
    try {
      const data = await productionTypeApi.getList()
      if (data && data.success) {
        productionTypes.value = data.data || []
        return productionTypes.value
      }
      return []
    } catch (err) {
      console.error('加载生产类型失败:', err)
      return []
    }
  }

  function addTempColor(color) {
    if (!tempColors.value.includes(color)) {
      tempColors.value.push(color)
    }
  }

  function removeTempColor(color) {
    tempColors.value = tempColors.value.filter(c => c !== color)
  }

  function clearTempColors() {
    tempColors.value = []
  }

  function setSearchParams(params) {
    searchParams.value = { ...searchParams.value, ...params }
  }

  function resetSearchParams() {
    searchParams.value = { search: '', categoryId: '' }
    pagination.value.page = 1
  }

  // ===== SKC 查找（支持对象格式） =====
  function findStyleBySkc(skc, styles) {
    if (!skc) return null

    for (const style of styles || globalStyles.value) {
      if (style.skc && Array.isArray(style.skc)) {
        for (const item of style.skc) {
          let code = item
          if (typeof item === 'object' && item !== null) {
            code = item.code
          }
          if (code === skc) {
            return style
          }
        }
      }
    }
    return null
  }

  // ===== 获取 SKC 对应的颜色 =====
  function getSkcColor(style, skc) {
    if (!style || !style.skc) return null
    for (const item of style.skc) {
      if (typeof item === 'object' && item !== null) {
        if (item.code === skc && item.color) {
          return item.color
        }
      }
    }
    return null
  }

  // ===== 获取 SKC 编码列表 =====
  function getSkcCodes(style) {
    if (!style || !style.skc) return []
    return style.skc.map(item => {
      if (typeof item === 'object' && item !== null) {
        return item.code
      }
      return item
    })
  }

  // ===== 规范化 SKC 数据 =====
  function normalizeSkc(skcArray) {
    if (!Array.isArray(skcArray)) return []
    return skcArray.map(item => {
      if (typeof item === 'object' && item !== null && item.code) {
        return item
      }
      return { code: item, color: null }
    })
  }

  // ✅ 加载款式
  async function loadGlobalStyles(params = {}) {
    try {
      const finalParams = {
        search: params.search !== undefined ? params.search : searchParams.value.search,
        categoryId: params.categoryId !== undefined ? params.categoryId : searchParams.value.categoryId,
        page: params.page || pagination.value.page,
        pageSize: params.pageSize || pagination.value.pageSize,
      }

      if (params.search !== undefined || params.categoryId !== undefined) {
        searchParams.value.search = finalParams.search
        searchParams.value.categoryId = finalParams.categoryId
        pagination.value.page = 1
      }

      const data = await styleApi.getGlobal(finalParams)
      if (data && data.success) {
        globalStyles.value = (data.data || []).map(row => ({
          ...row,
          skc: normalizeSkc(row.skc)
        }))
        if (data.pagination) {
          pagination.value = data.pagination
        }
        return globalStyles.value
      }
      return []
    } catch (err) {
      console.error('加载全局款式失败:', err)
      return []
    }
  }

  async function searchStyles(searchText) {
    searchParams.value.search = searchText
    pagination.value.page = 1
    return await loadGlobalStyles()
  }

  async function filterByCategory(categoryId) {
    searchParams.value.categoryId = categoryId
    pagination.value.page = 1
    return await loadGlobalStyles()
  }

  async function goToPage(page) {
    pagination.value.page = page
    return await loadGlobalStyles()
  }

  async function createStyle(data) {
    try {
      const submitData = {
        ...data,
        skc: normalizeSkc(data.skc)
      }
      const result = await styleApi.create(submitData)
      if (result && result.success) {
        await loadGlobalStyles()
        return result.data
      }
      return null
    } catch (err) {
      console.error('创建款式失败:', err)
      throw err
    }
  }

  async function updateStyle(data) {
    try {
      const submitData = {
        ...data,
        skc: normalizeSkc(data.skc)
      }
      const result = await styleApi.update(submitData)
      if (result && result.success) {
        await loadGlobalStyles()
        return result.data
      }
      return null
    } catch (err) {
      console.error('更新款式失败:', err)
      throw err
    }
  }

  async function deleteStyle(id) {
    try {
      const result = await styleApi.delete(id)
      if (result && result.success) {
        await loadGlobalStyles()
        return true
      }
      return false
    } catch (err) {
      console.error('删除款式失败:', err)
      throw err
    }
  }

  return {
    // State
    globalStyles,
    tempColors,
    selectedStyleId,
    pagination,
    searchParams,
    categories,
    productionTypes,

    // Getters
    getStyleById,
    getStyleByName,
    getStyleName,
    level1List,
    level1Options,
    categoryFilterOptions,
    productionTypeOptions,

    // SKC 方法
    findStyleBySkc,
    getSkcColor,
    getSkcCodes,
    normalizeSkc,

    // 分类方法
    getLevel2ByLevel1Id,
    getLevel2Options,
    getAllLevel2Options,
    loadCategories,
    loadProductionTypes,

    // Actions
    addTempColor,
    removeTempColor,
    clearTempColors,
    setSearchParams,
    resetSearchParams,
    loadGlobalStyles,
    searchStyles,
    filterByCategory,
    goToPage,
    createStyle,
    updateStyle,
    deleteStyle
  }
})